import React, { Component } from 'react';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { parseDateFromISO8601, stringifyDateToISO8601 } from '../../../util/dates';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import DateTimeFields, { parseTimesParam } from './DateTimeFields';
import FilterPopupForSidebar from './FilterPopupForSidebar';
import css from './BookingDateRangeFilter.module.css';

const getDatesQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'dates';
};

/**
 * Read the URL params back into form values.
 *
 * `dates` still looks like "2026-09-08,2026-09-08" so the parameter shape is unchanged,
 * but only one day is meaningful now, so the first value wins. `times` carries the hours.
 *
 * @param {String} datesValue - the "dates" query parameter
 * @param {String} timesValue - the "times" query parameter
 * @returns {Object} { date, startTime, endTime }
 */
const parseValue = (datesValue, timesValue) => {
  const rawDates = datesValue ? `${datesValue}`.split(',') : [];
  const date = rawDates[0] ? parseDateFromISO8601(rawDates[0]) : null;
  const { startTime, endTime } = parseTimesParam(timesValue);
  return { date: date || null, startTime, endTime };
};

/**
 * Turn the form values into query params.
 *
 * The day and both times are all required: a day on its own cannot be turned into an
 * exact availability window, so a partial selection clears the filter rather than
 * silently searching the whole day. Nulls clear the params, which is also what the
 * popup's "Clear" button produces.
 *
 * The times are wall-clock times in the reader's own zone, so the zone travels with them
 * as `tz`. SearchPage.duck needs it to turn "09:00" into an instant, and resolving it
 * here keeps a shared link meaning the same hours for whoever opens it.
 *
 * @param {Object} values - { date, startTime, endTime }
 * @param {String} queryParamName - usually "dates"
 * @returns {Object} query params
 */
const formatValue = (values, queryParamName) => {
  const { date, startTime, endTime } = values || {};
  const day = date ? stringifyDateToISO8601(date) : null;
  const isComplete = !!day && !!startTime && !!endTime;

  if (!isComplete) {
    return { [queryParamName]: null, times: null, tz: null };
  }

  const timeZone =
    typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null;

  return {
    [queryParamName]: `${day},${day}`,
    times: `${startTime},${endTime}`,
    tz: timeZone || null,
  };
};

/**
 * BookingDateRangeFilter component
 *
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.id - The ID
 * @param {React.Node} [props.label] - The label
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {boolean} [props.liveEdit] - Whether to live edit
 * @param {Array<string>} [props.queryParamNames] - The query param names (e.g. ['dates'])
 * @param {Function} props.onSubmit - The function to submit
 * @param {Object} [props.initialValues] - The initial values
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
export class BookingDateRangeFilterComponent extends Component {
  constructor(props) {
    super(props);

    this.state = { isOpen: true };

    this.popupControllerRef = null;
    this.plainControllerRef = null;

    this.toggleIsOpen = this.toggleIsOpen.bind(this);
  }

  toggleIsOpen() {
    this.setState(prevState => ({ isOpen: !prevState.isOpen }));
  }

  render() {
    const {
      className,
      rootClassName,
      showAsPopup = true,
      isDesktop = false,
      initialValues,
      id,
      contentPlacementOffset = 0,
      onSubmit,
      queryParamNames,
      label,
      intl,
      getAriaLabel = () => {},
      ...rest
    } = this.props;

    const datesQueryParamName = getDatesQueryParamName(queryParamNames);
    const initialDates = parseValue(initialValues?.[datesQueryParamName], initialValues?.times);

    // The filter only counts as applied once the day and both times are set, which is the
    // same rule formatValue enforces when writing the params back.
    const isSelected = !!initialDates.date && !!initialDates.startTime && !!initialDates.endTime;
    const selectedDate = initialDates.date;

    const format = {
      month: 'short',
      day: 'numeric',
    };

    const formattedDate = selectedDate ? intl.formatDate(selectedDate, format) : null;
    // e.g. "8 sep, 09:00-10:00"
    const formattedSelection = isSelected
      ? `${formattedDate}, ${initialDates.startTime}\u2013${initialDates.endTime}`
      : null;

    const labelForPlain = isSelected
      ? intl.formatMessage(
          { id: 'BookingDateRangeFilter.labelSelectedPlain' },
          {
            dates: formattedSelection,
          }
        )
      : label
      ? label
      : intl.formatMessage({ id: 'BookingDateRangeFilter.labelPlain' });

    const labelForPopup = isSelected
      ? intl.formatMessage(
          { id: 'BookingDateRangeFilter.labelSelectedPopup' },
          {
            dates: formattedSelection,
          }
        )
      : label
      ? label
      : intl.formatMessage({ id: 'BookingDateRangeFilter.labelPopup' });

    const labelSelection = isSelected
      ? intl.formatMessage(
          { id: 'BookingDateRangeFilter.labelSelectedPopup' },
          {
            dates: formattedSelection,
          }
        )
      : null;

    const handleSubmit = values => {
      onSubmit(formatValue(values, datesQueryParamName));
    };

    const onClearPopupMaybe =
      this.popupControllerRef && this.popupControllerRef.onReset
        ? { onClear: () => this.popupControllerRef.onReset(null) }
        : {};

    const onCancelPopupMaybe =
      this.popupControllerRef && this.popupControllerRef.onReset
        ? { onCancel: () => this.popupControllerRef.onReset(selectedDate) }
        : {};

    const onClearPlainMaybe =
      this.plainControllerRef && this.plainControllerRef.onReset
        ? { onClear: () => this.plainControllerRef.onReset(null) }
        : {};

    return showAsPopup ? (
      <FilterPopup
        className={className}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        label={labelForPopup}
        isSelected={isSelected}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPopupMaybe}
        {...onCancelPopupMaybe}
        initialValues={initialDates}
        ariaLabel={getAriaLabel(label, labelForPopup)}
        {...rest}
      >
        <DateTimeFields
          controllerRef={node => {
            this.popupControllerRef = node;
          }}
        />
      </FilterPopup>
    ) : isDesktop ? (
      <FilterPopupForSidebar
        className={className}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        label={label}
        labelSelection={labelSelection}
        isSelected={isSelected}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPopupMaybe}
        {...onCancelPopupMaybe}
        ariaLabel={getAriaLabel(label, labelForPopup)}
        initialValues={initialDates}
        {...rest}
      >
        <DateTimeFields
          controllerRef={node => {
            this.popupControllerRef = node;
          }}
        />
      </FilterPopupForSidebar>
    ) : (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={label}
        labelSelection={labelSelection}
        labelSelectionSeparator=":"
        isSelected={isSelected}
        id={`${id}.plain`}
        liveEdit
        onSubmit={handleSubmit}
        {...onClearPlainMaybe}
        initialValues={initialDates}
        ariaLabel={getAriaLabel(label, labelForPopup)}
        {...rest}
      >
        <DateTimeFields
          controllerRef={node => {
            this.plainControllerRef = node;
          }}
        />
      </FilterPlain>
    );
  }
}

const BookingDateRangeFilter = injectIntl(BookingDateRangeFilterComponent);

export default BookingDateRangeFilter;
